import { useNavigate } from "react-router-dom";
import { TextField, Button, CardMedia, Input } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { ChangeEvent, useState, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import { useUser } from "../../hooks/useUser";
import { useUpdateAdminProfileMutation } from "../../store/api/userAPI";
import { useUploadFileMutation } from "../../store/api/commonAPI";
import { showToast, showError } from "../../constants/toasts";

const EditProfile = () => {

  const navigate = useNavigate();
  const { profile, refetchProfile } = useUser();
  const [updateAdminProfile, { isLoading: isUpdating }] = useUpdateAdminProfileMutation();
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();

  const [image, setImage] = useState<string>("");
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>("");
  
  // Validation errors
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    phone?: string;
  }>({});

  // Set profile data when available
  useEffect(() => {
    if (profile) {
      if (profile.image) {
        setImage(profile.image);
        setUploadedImageUrl(profile.image);
      }
      setName(profile.fullName || "");
      setEmail(profile.email || "");
      // Combine country code and phone for PhoneInput
      const countryCode = profile.countryCode?.replace("+", "") || "";
      const phoneNumber = profile.phone || "";
      setPhone(countryCode + phoneNumber);
      setSelectedCountryCode(profile.countryCode || "");
    }
  }, [profile]);

  // Handle image upload when file is selected
  const handleImageUpload = async (file: File) => {
    // Validate file type
    if (!file.type.includes("image")) {
      showError("Please add image only.");
      return;
    }
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError("Image size must be less than 5MB.");
      return;
    }

    // Show preview immediately
    setImage(URL.createObjectURL(file));

    try {
      // Upload image to server
      const uploadResult = await uploadFile({ file }).unwrap();
      const imageUrl = uploadResult.data?.data || uploadResult.data;
      
      if (imageUrl) {
        setUploadedImageUrl(imageUrl);
        showToast("Image uploaded successfully!");
      } else {
        showError("Failed to get image URL from server.");
        // Revert to previous image
        setImage(profile?.image || "");
      }
    } catch (uploadError: any) {
      showError(uploadError?.data?.message || "Failed to upload image. Please try again.");
      // Revert to previous image
      setImage(profile?.image || "");
      setUploadedImageUrl(profile?.image || "");
    }
  };

  // Validation functions
  const validateName = (value: string): string | undefined => {
    if (!value || value.trim() === "") {
      return "Name is required";
    }
    if (value.trim().length < 2) {
      return "Name must be at least 2 characters";
    }
    if (value.trim().length > 50) {
      return "Name must be less than 50 characters";
    }
    return undefined;
  };

  const validateEmail = (value: string): string | undefined => {
    if (!value || value.trim() === "") {
      return "Email is required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value.trim())) {
      return "Please enter a valid email address";
    }
    return undefined;
  };

  const validatePhone = (value: string): string | undefined => {
    if (!value || value.trim() === "") {
      return "Phone number is required";
    }
    // PhoneInput includes country code, so we check if it's at least 10 digits (excluding country code)
    const digitsOnly = value.replace(/\D/g, "");
    if (digitsOnly.length < 10) {
      return "Please enter a valid phone number";
    }
    return undefined;
  };

  // Split full name into first and last name
  const splitName = (fullName: string): { firstName: string; lastName: string } => {
    const trimmedName = fullName.trim();
    if (!trimmedName) {
      return { firstName: "", lastName: "" };
    }
    const parts = trimmedName.split(/\s+/);
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: "" };
    }
    const firstName = parts[0];
    const lastName = parts.slice(1).join(" ");
    return { firstName, lastName };
  };

  // Parse phone number to get country code and phone
  const parsePhoneNumber = (phoneValue: string, countryCode: string): { countryCode: string; phone: string } => {
    if (!phoneValue) {
      return { countryCode: countryCode || "", phone: "" };
    }
    
    // PhoneInput format: countryCode + phoneNumber (e.g., "911234567890" for India)
    // We need to extract country code and phone number
    const digitsOnly = phoneValue.replace(/\D/g, "");
    if (digitsOnly.length === 0) {
      return { countryCode: countryCode || "", phone: "" };
    }
    
    // Use the selected country code if available
    if (countryCode) {
      const codeDigits = countryCode.replace(/\D/g, "");
      if (digitsOnly.startsWith(codeDigits)) {
        return {
          countryCode: countryCode.startsWith("+") ? countryCode : `+${countryCode}`,
          phone: digitsOnly.substring(codeDigits.length),
        };
      }
    }
    
    // Fallback: try to detect country code
    // Common country codes
    if (digitsOnly.startsWith("1") && digitsOnly.length > 10) {
      return { countryCode: "+1", phone: digitsOnly.substring(1) };
    } else if (digitsOnly.startsWith("91") && digitsOnly.length > 10) {
      return { countryCode: "+91", phone: digitsOnly.substring(2) };
    } else if (digitsOnly.length > 10) {
      // Try first 2 digits as country code
      return { countryCode: `+${digitsOnly.substring(0, 2)}`, phone: digitsOnly.substring(2) };
    }
    
    // Use profile country code as fallback
    return {
      countryCode: countryCode || profile?.countryCode || "",
      phone: digitsOnly,
    };
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Clear previous errors
    setErrors({});

    // Validate all fields
    const nameError = validateName(name);
    const emailError = validateEmail(email);
    const phoneError = validatePhone(phone);

    if (nameError || emailError || phoneError) {
      setErrors({
        name: nameError,
        email: emailError,
        phone: phoneError,
      });
      if (nameError) showError(nameError);
      if (emailError) showError(emailError);
      if (phoneError) showError(phoneError);
      return;
    }

    try {
      // Split name into first and last name
      const { firstName, lastName } = splitName(name);

      // Parse phone number
      const { countryCode, phone: phoneNumber } = parsePhoneNumber(phone, selectedCountryCode);

      // Prepare update data - use uploadedImageUrl if available, otherwise use current image
      const updateData = {
        fullName: name.trim(),
        firstName: firstName,
        lastName: lastName,
        email: email.trim(),
        image: uploadedImageUrl || image || "",
        phone: phoneNumber || "",
        countryCode: countryCode || "",
      };

      // Call update API
      const result = await updateAdminProfile(updateData).unwrap();
      
      // Show success message
      showToast(result.message || "Profile updated successfully!");
      
      // Refetch profile to update the UI
      refetchProfile();
      
      // Navigate back to profile page after a short delay
      setTimeout(() => {
        navigate("/manage-profile");
      }, 1500);
    } catch (error: any) {
      // Show error message
      const errorMessage = error?.data?.message || error?.message || "Failed to update profile. Please try again.";
      showError(errorMessage);
    }
  };

  return (
    <>
      <div className="main_title">
        <h1>Edit Profile</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button>
          {' '} - <button onClick={() => navigate("/manage-profile")}>Manage Profile</button>
          {' '} - Edit Profile
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <div className="control_group w_100">
              <label htmlFor="bannerimage">Upload Image</label>
              {isUploading && (
                <div style={{ marginBottom: "10px", color: "#666" }}>
                  Uploading image...
                </div>
              )}
              {image ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia
                        component="img"
                        image={image}
                        alt="photo"
                      />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        setImage("");
                        setUploadedImageUrl("");
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label
                  className="upload_image"
                  htmlFor="icon-button-file"
                >
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{
                      accept: "image/png,image/jpeg",
                    }}
                    onChange={(
                      event: ChangeEvent<HTMLInputElement>
                    ) => {
                      const files = (
                        event.target as HTMLInputElement
                      ).files;
                      if (files && files[0]) {
                        handleImageUpload(files[0]);
                      }
                      // Reset input value to allow selecting the same file again
                      event.target.value = "";
                    }}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img
                        src="/static/images/user_placeholder.png"
                        alt="icon"
                      />
                      <span className="upload_img_icon">
                        <img
                          src="/static/images/edit2_icon.svg"
                          alt="icon"
                        />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>
            <div className="control_group w_33">
              <label htmlFor="name">Name <span style={{ color: "red" }}>*</span></label>
              <TextField 
                id="name" 
                placeholder="Enter Name" 
                hiddenLabel 
                fullWidth 
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) {
                    setErrors({ ...errors, name: undefined });
                  }
                }}
                error={!!errors.name}
                helperText={errors.name || " "}
                FormHelperTextProps={{
                  style: { margin: 0, minHeight: errors.name ? "auto" : "20px" }
                }}
              />
            </div>
            <div className="control_group w_33">
              <label htmlFor="email">Email Id <span style={{ color: "red" }}>*</span></label>
              <TextField 
                id="email" 
                placeholder="Enter Email Id" 
                hiddenLabel 
                fullWidth 
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) {
                    setErrors({ ...errors, email: undefined });
                  }
                }}
                error={!!errors.email}
                helperText={errors.email || " "}
                FormHelperTextProps={{
                  style: { margin: 0, minHeight: errors.email ? "auto" : "20px" }
                }}
              />
            </div>
           <div className="control_group w_33">
                <label htmlFor="phone">Phone Number <span style={{ color: "red" }}>*</span></label>
                <PhoneInput
                  country={profile?.countryCode?.replace("+", "") || "in"}
                  value={phone}
                  onChange={(value, country: any) => {
                    setPhone(value);
                    // Extract country code from country object
                    if (country && country.dialCode) {
                      setSelectedCountryCode(`+${country.dialCode}`);
                    }
                    if (errors.phone) {
                      setErrors({ ...errors, phone: undefined });
                    }
                  }}
                  placeholder="Enter Phone Number"
                  enableSearch={false}
                  autoFormat={false}
                  inputStyle={{ 
                    width: "100%",
                    border: errors.phone ? "1px solid #d32f2f" : undefined,
                    borderRadius: "4px"
                  }}
                />
                {errors.phone && (
                  <span style={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: "3px", display: "block" }}>
                    {errors.phone}
                  </span>
                )}
              </div>
          </div>
          <div className="form_btn">
            <Button 
              className="btn btn_primary" 
              onClick={handleSubmit}
              disabled={isUpdating || isUploading}
            >
              {isUpdating ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditProfile;
